import { formFieldBaseStylesModal650, formFieldNameBaseStyles } from '@app/styles/base-styles';
import {
  Box,
  Button,
  CustomDatePicker,
  EasyFilterView,
  FormLabel,
  Input,
  Select,
  TextArea,
  Toggle,
} from '@ui';

import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { Filter, FilterQmOperatorsType, Range } from '@app/components/ui/easy-filter/types';
import { OptionItem } from '@app/components/ui/select/select.type';
import { MonitoringFormTaskMutationType } from '@app/interfaces/qm-module/qm-manage/qm-monioring-tasks.type';
import { useGetChatProjectMetricsListQuery } from '@app/store/api/chat-api/chat-metric.api';
import { useGetProjectMetricsListQuery } from '@app/store/api/metrics.api';
import {
  useAddMFTaskMutation,
  useEditMFTaskMutation,
} from '@app/store/api/qm-module/qm-manage/qm-manage-monitoring-tasks.api';
import { Controller, useForm } from 'react-hook-form';
import useTranslation from '@app/hooks/use-translation';
import classNames from 'classnames';
import { useNavigate, useOutletContext } from 'react-router';

import { useGetGptGroupsWithMetricsQuery } from '@app/store/api/gpt-metric.api';

import { MonitoringTaskFormOutlet } from './blocks/monitoring-task-settings-layout';
type FormType = MonitoringFormTaskMutationType;

const MonitoringTaskMainSettings = () => {
  const { t } = useTranslation('pages.qmManage.monitoring_tasks.form');
  const { t: tForm } = useTranslation('common.forms');
  const { mFList, mFTaskObject, teamsAndOperatorsDataForFilter, isNew } =
    useOutletContext<MonitoringTaskFormOutlet>();
  const navigate = useNavigate();

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    control,
    clearErrors,
    formState: { errors },
  } = useForm<FormType>({
    defaultValues: mFTaskObject
      ? { ...mFTaskObject }
      : {
          settings: {
            set_num: { type: 'NDAY', value: 0 },
            qm_operators: [],
            qm_teams: [],
            dncm: false,
            dnis: false,
            op_approve: false,
            operator_bind: false,
          },
          rules: { filter: [], range: { type: 'n', parameters: [] } },
        },
  });

  const [dtStartTime, setDtStartTime] = useState(mFTaskObject?.dt_start?.split(' ')[1].slice(0, 5));
  const [dtEndTime, setDtEndTime] = useState(mFTaskObject?.dt_end?.split(' ')[1].slice(0, 5));
  const curSelectedMF = mFList.find((form) => form.id === watch('mform_id'));

  // api
  const [addTask] = useAddMFTaskMutation();
  const [editTask] = useEditMFTaskMutation();
  const { data: voiceMetricList, isLoading: voiceProjectMetricLoading } =
    useGetProjectMetricsListQuery(
      {
        project_id: curSelectedMF?.project.project_id || '',
      },
      {
        skip: curSelectedMF?.project.project_type === 'chats' || !curSelectedMF?.project.project_id,
        refetchOnMountOrArgChange: true,
      },
    );

  const { data: chatMetricList, isLoading: chatProjectMetricLoading } =
    useGetChatProjectMetricsListQuery(
      {
        project_id: curSelectedMF?.project.project_id || '',
      },
      {
        skip: curSelectedMF?.project.project_type === 'voice' || !curSelectedMF?.project.project_id,
        refetchOnMountOrArgChange: true,
      },
    );
  const { data: gptMetricList, isLoading: gptMetricLoading } = useGetGptGroupsWithMetricsQuery(
    {
      params: {
        project_id: curSelectedMF?.project.project_id || '',
      },
    },
    {
      skip: curSelectedMF?.project.project_type === 'chats' || !curSelectedMF?.project.project_id,
      refetchOnMountOrArgChange: true,
    },
  );
  const gpt_metrics = useMemo(
    () => gptMetricList?.gpt_groups.flatMap((group) => group.gpt_metrics) || [],
    [gptMetricList?.gpt_groups],
  );

  // api
  function onSubmit(formResponse: FormType) {
    const data = {
      ...formResponse,
      dt_start: dtStartTime
        ? moment(formResponse.dt_start.split(' ')[0] + ' ' + dtStartTime + ':00').format(
            'yyyy-MM-DD HH:mm:ss',
          )
        : formResponse.dt_start,
      dt_end: dtEndTime
        ? moment(formResponse.dt_end.split(' ')[0] + ' ' + dtEndTime + ':00').format(
            'yyyy-MM-DD HH:mm:ss',
          )
        : formResponse.dt_end,
    };
    const editCheckedObject = Object.entries(data).reduce((acc, [curkey, curVal]) => {
      if (JSON.stringify(curVal) !== JSON.stringify(mFTaskObject?.[curkey])) {
        acc[curkey] = curVal;
      }
      return acc;
    }, {});

    !isNew && mFTaskObject
      ? editTask({
          body: editCheckedObject,
          params: { id: mFTaskObject.id },
        })
          .unwrap()
          .then(() => navigate(`../`))
      : addTask(data)
          .unwrap()
          .then((task) => navigate(`../${task.id}`));
  }

  const monitoringFormsOptions: OptionItem[] = mFList.map((MF) => ({
    title: MF.name,
    value: MF.id,
  }));
  const filteringKeys = useMemo(() => {
    return [...(voiceMetricList || chatMetricList || []), ...(gpt_metrics || [])].map((metric) => ({
      id: metric.metric_id,
      title: metric.name,
      type_value: metric.result_value_type,
    }));
  }, [chatMetricList, gpt_metrics, voiceMetricList]);

  function onChangeFilterHandler({
    filter,
    range,
    operators,
  }: Filter & Range & Partial<FilterQmOperatorsType>) {
    setValue('rules', { filter, range });
    const operatorsResult = operators
      ?.filter((i) => !!i.value[0])
      .reduce<Record<'qm_teams' | 'qm_operators', string[]>>(
        (memo, item) => {
          memo[item.type] = [
            ...(memo[item.type] || []),
            ...item.value.filter((i) => !memo?.[item.type]?.includes(i)),
          ];
          return memo;
        },
        { qm_teams: [], qm_operators: [] },
      );
    setValue('settings.qm_teams', operatorsResult?.qm_teams || []);
    setValue('settings.qm_operators', operatorsResult?.qm_operators || []);
  }

  const loadOptions: OptionItem[] = [
    { value: 'NDAY', title: t('NDAY') },
    { value: 'PDAY', title: t('PDAY') },
    { value: 'PHOUR', title: t('PHOUR') },
  ];

  return (
    <Box className="mt-[42px] p-[45px_0px_0px_75px]">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full flex flex-col gap-[25px] relative max-w-[65%]"
      >
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx required label={t('name')} />
          <Input
            {...register('name', {
              required: { value: true, message: tForm('required') },
              maxLength: { value: 100, message: tForm('max_len_error', { num: 100 }) },
            })}
            error={errors.name?.message}
          />
        </div>
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx label={t('description')} />
          <TextArea
            {...register('description', {
              maxLength: { value: 200, message: tForm('max_len_error', { num: 200 }) },
            })}
            defaultValue={watch('description')}
            height={100}
            error={errors.description?.message}
          />
        </div>
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx label={t('interval_start')} />
          <div className="grid items-center gap-x-[10px] grid-cols-[1fr,70px]">
            <CustomDatePicker
              isFull
              withoutDefault
              onChange={(start) => {
                // eslint-disable-next-line no-debugger
                // debugger;
                setValue('dt_start', moment(start, 'DD.MM.yyyy').format('yyyy-MM-DD'));
              }}
              selected={
                watch('dt_start') ? moment(watch('dt_start'), 'yyyy-MM-DD').toDate() : undefined
              }
            />
            <Input
              mask="99:99"
              defaultValue={dtStartTime}
              onChange={(event) => setDtStartTime(event.target.value)}
            />
          </div>
        </div>
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx label={t('interval_end')} />
          <div className="grid items-center gap-x-[10px] grid-cols-[1fr,70px]">
            <CustomDatePicker
              isFull
              withoutDefault
              onChange={(end) => {
                setValue('dt_end', moment(end, 'DD.MM.yyyy').format('yyyy-MM-DD'));
              }}
              selected={
                watch('dt_end') ? moment(watch('dt_end'), 'yyyy-MM-DD').toDate() : undefined
              }
            />
            <Input
              mask="99:99"
              defaultValue={dtEndTime}
              onChange={(event) => setDtEndTime(event.target.value)}
            />
          </div>
        </div>
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx required label={t('mForm')} />
          <Controller
            render={({ fieldState: { error } }) => (
              <Select
                disable={mFTaskObject?.status === 'PAUSE'}
                options={monitoringFormsOptions}
                defaultValue={watch('mform_id')}
                onChange={(value) => {
                  setValue('mform_id', String(value));
                  clearErrors('mform_id');
                }}
                error={error?.message}
              />
            )}
            name="mform_id"
            rules={{
              validate: (val) => !!val || tForm('required'),
            }}
            control={control}
          />
        </div>
        <div className={classNames(formFieldBaseStylesModal650, '!items-start')}>
          <div className="mt-[2px]">
            <LabelMiniJsx label={t('rec_select')} />
          </div>
          <EasyFilterView
            modalMode
            disableSaveButton={mFTaskObject?.status === 'PAUSE'}
            onChangeHandler={onChangeFilterHandler}
            keysForFiltering={filteringKeys || []}
            teamsAndOperatorsDataForFilter={teamsAndOperatorsDataForFilter}
            isLoading={voiceProjectMetricLoading || chatProjectMetricLoading || gptMetricLoading}
            data={{
              ...watch('rules'),
              operators: [
                ...watch('settings.qm_operators').map((id) => ({
                  value: [id],
                  type: 'qm_operators' as const,
                })),
                ...watch('settings.qm_teams').map((id) => ({
                  value: [id],
                  type: 'qm_teams' as const,
                })),
              ],
            }}
          />
        </div>
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx label={t('operator_bind')} />
          <div className="flex items-center gap-[10px]">
            <Toggle
              disabled={mFTaskObject?.status === 'PAUSE'}
              checked={watch('settings.operator_bind')}
              onChecked={(val) => setValue('settings.operator_bind', val)}
            />
            <span className="-mb-[15px] text-3color text-[11px]">{t('operator_bind_desc')}</span>
          </div>
        </div>
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx label={t('load_distribution')} />
          <div className="grid w-full grid-cols-[240px_80px] items-center gap-x-[30px]">
            <Select
              options={loadOptions}
              defaultValue={watch('settings.set_num.type')}
              onChange={(val) =>
                setValue('settings.set_num.type', val as 'NDAY' | 'PDAY' | 'PHOUR')
              }
            />
            <Input {...register('settings.set_num.value')} type="number" />
          </div>
        </div>
        {/*  */}
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx className="w-[70%]" label={t('dncm')} />
          <Toggle
            disabled={mFTaskObject?.status === 'PAUSE'}
            checked={watch('settings.dncm')}
            onChecked={(val) => setValue('settings.dncm', val)}
          />
        </div>
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx className="w-[70%]" label={t('dnis')} />
          <Toggle
            disabled={mFTaskObject?.status === 'PAUSE'}
            checked={watch('settings.dnis')}
            onChecked={(val) => setValue('settings.dnis', val)}
          />
        </div>
        <div className={formFieldBaseStylesModal650}>
          <LabelMiniJsx label={t('op_approve')} />
          <Toggle
            disabled={mFTaskObject?.status === 'PAUSE'}
            checked={watch('settings.op_approve')}
            onChecked={(val) => setValue('settings.op_approve', val)}
          />
        </div>
        {/*  */}
        {/*BUTTON BLOCK*/}
        <div className="sticky bg-white bottom-0 py-[20px] flex items-center gap-[10px]">
          <Button
            disabled={
              isNew ? false : mFTaskObject?.status !== 'PAUSE' && mFTaskObject?.status !== 'NEW'
            }
            type="submit"
            label={t('approve_button')}
          />
          <Button fill="linked" link="./.." label={t('cancel_button')} />
        </div>
        {/*BUTTON BLOCK*/}
      </form>
    </Box>
  );
};

const LabelMiniJsx = ({
  label,
  className,
  required,
}: {
  className?: string;
  label: string;
  required?: boolean;
}) => (
  <FormLabel
    required={required}
    label={label}
    className={classNames(formFieldNameBaseStyles, className)}
  />
);

export default MonitoringTaskMainSettings;
